import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import {
	NavigationInstruction,
	Next,
	PipelineStep,
	RedirectToRoute,
} from 'aurelia-router';
import httpStatus from 'http-status';

import { LoginApi } from '~/api/login-api';
import { AppRouteID } from '~/app-routes';
import { Authentication } from '~/authentication/authentication';
import { NavigationState } from '~/components/navigation/navigation-state';
import { CommonURLParam } from '~/constants';
import { OktaLogin } from '~/login/okta-login';
import { SettingsState } from '~/settings-state/settings-state';
import { FetchUtils } from '~/utils/fetch-utils';
import { clearLocalLoginData } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';

@autoinject
export class InitialLoadStep implements PipelineStep {
	constructor(
		private readonly navState: NavigationState,
		private readonly settingsState: SettingsState,
		private readonly loginApi: LoginApi,
		private readonly oktaLogin: OktaLogin,
		private readonly i18n: I18N,
		private readonly authentication: Authentication,
	) {}

	public run(navigationInstruction: NavigationInstruction, next: Next) {
		// we are checking loggedIn state via local storage because when a
		// WEBUI form is loaded in tree view for the first time, App Store
		// has not yet dispatched LoginUser action to update StateHistory
		if (
			!this.authentication.isLoggedIn() ||
			this.authentication.isLoginExpired()
		) {
			return next();
		}
		const queryParams = navigationInstruction.queryParams;
		// Get 'nav-view'
		let navView = '';
		if (queryParams[CommonURLParam.NAV_VIEW]) {
			navView = queryParams[CommonURLParam.NAV_VIEW];
		}
		return Promise.all([
			this.navState.maybeFetchNavigation(navView),
			this.settingsState.fetchSettings(),
		])
			.then(() => {
				Logger.setLogLevelThreshold(
					this.settingsState.state.frontendLogLevel,
				);
				return next();
			})
			.catch((error) => {
				const logoutStatuses: number[] = [
					httpStatus.FORBIDDEN,
					httpStatus.UNAUTHORIZED,
					httpStatus.INTERNAL_SERVER_ERROR,
				];
				if (this.errorMatchesStatus(error, ...logoutStatuses)) {
					// NOTE(Jae): 2020-02-24
					// If there are errors, log the user out to avoid a recursive loop of fetching
					// navigation JSON.
					let message = FetchUtils.normalizeError(error);

					let logout;
					if (OktaLogin.isLoggedIn()) {
						message = this.i18n.tr('errors.sso-failed');
						logout = () => this.oktaLogin.logout();
					} else {
						logout = () => this.loginApi.logout();
					}
					return logout().then(() => {
						// need to also clear local login data otherwise we still get into an infinite login loop
						clearLocalLoginData();
						return next.cancel(
							new RedirectToRoute(
								AppRouteID.Login,
								{ errorMessage: message },
								{ replace: true, trigger: true },
							),
						);
					});
				}

				if (this.errorMatchesStatus(error, httpStatus.BAD_REQUEST)) {
					// Go to an error page but do not log out
					return next.cancel(
						new RedirectToRoute(
							'error',
							{ errorMessage: FetchUtils.normalizeError(error) },
							{ replace: true, trigger: true },
						),
					);
				}

				return Promise.reject(error);
			});
	}

	private errorMatchesStatus(
		error: any,
		...statusesToMatch: number[]
	): boolean {
		if (!error) {
			return false;
		}

		const errorStatus: number = error.status;
		if (errorStatus && statusesToMatch.includes(errorStatus)) {
			return true;
		}

		const errorMessage: string = error.message;
		if (errorMessage) {
			for (const status of statusesToMatch) {
				if (errorMessage.trimEnd().endsWith(String(status))) {
					return true;
				}
			}
		}

		return false;
	}
}
